import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Services() {
  const { elementRef: headerRef } = useScrollAnimation();
  const { elementRef: ref0 } = useScrollAnimation({ delay: 0 });
  const { elementRef: ref1 } = useScrollAnimation({ delay: 100 });
  const { elementRef: ref2 } = useScrollAnimation({ delay: 200 });

  const cardRefs = [ref0, ref1, ref2];

  const packages = [
    {
      name: 'Foundation',
      price: 397,
      duration: 'month',
      description: 'Perfect for those starting their transformation journey',
      image: 'https://public.readdy.ai/ai/img_res/eed363d6e67b4975b4e0030061271729.jpg',
      features: [
        'Custom Training Program',
        'Personalized Nutrition Plan',
        'Bi-Weekly Check-Ins',
        'Email Support',
        'Progress Tracking',
      ],
      bestFor: 'Beginners to intermediate',
      cta: 'Get Started',
      popular: false,
    },
    {
      name: 'Ascend',
      price: 597,
      duration: 'month',
      description: 'The complete system for serious transformation',
      image: 'https://readdy.ai/api/search-image?query=athletic%20man%20performing%20barbell%20squat%20in%20professional%20gym%20setting%20intense%20strength%20training%20with%20perfect%20form%20clean%20simple%20background%20natural%20lighting%20showing%20power%20and%20determination%20fitness%20photography&width=800&height=600&seq=service-ascend-man-001&orientation=landscape',
      features: [
        'Everything in Foundation',
        'Weekly 1:1 Video Check-Ins',
        'Form Review & Feedback',
        'Direct Message Support',
        'Private Community Access',
        'Supplement Guidance',
      ],
      bestFor: 'Committed individuals ready to level up',
      cta: 'Start Transformation',
      popular: true,
    },
    {
      name: 'Legacy Elite',
      price: 997,
      duration: 'month',
      description: 'Premium coaching for high performers',
      image: 'https://readdy.ai/api/search-image?query=diverse%20group%20of%20athletes%20training%20together%20in%20premium%20gym%20both%20men%20and%20women%20working%20out%20side%20by%20side%20professional%20fitness%20environment%20with%20clean%20simple%20background%20showing%20teamwork%20and%20elite%20performance%20natural%20lighting&width=800&height=600&seq=service-elite-diverse-001&orientation=landscape',
      features: [
        'Everything in Ascend',
        '24/7 Priority Support',
        'Twice-Weekly Video Calls',
        'Advanced Performance Tracking',
        'Lifestyle & Mindset Coaching',
        'VIP Community Access',
        'Monthly Strategy Sessions',
      ],
      bestFor: 'Elite performers who demand the best',
      cta: 'Apply Now',
      popular: false,
    },
  ];

  return (
    <section id="services" className="py-20 sm:py-28" style={{ background: '#ffffff' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div ref={headerRef} className="animate-fade-up text-center mb-16">
          <p className="text-xs font-bold tracking-[0.35em] uppercase mb-3" style={{ color: '#B8860B' }}>
            Choose Your Path
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-5 leading-tight text-gray-900">
            Programs Built For People Who Are Serious
          </h2>
          <p className="text-sm sm:text-base max-w-2xl mx-auto" style={{ color: '#666666' }}>
            Every package is designed to deliver real results. Pick the level that matches your commitment.
          </p>
          <div className="w-16 h-1 mx-auto rounded-full mt-6" style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)' }} />
        </div>

        {/* Package Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {packages.map((pkg, idx) => (
            <div
              key={idx}
              ref={cardRefs[idx]}
              className="animate-fade-up bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 relative"
              style={{
                border: pkg.popular ? '2px solid #B8860B' : '1px solid #ebebeb',
                boxShadow: pkg.popular
                  ? '0 20px 60px rgba(184,134,11,0.15)'
                  : '0 4px 20px rgba(0,0,0,0.04)',
              }}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div
                  className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)', color: '#000000' }}
                >
                  MOST POPULAR
                </div>
              )}

              {/* Image */}
              <div className="relative w-full h-56 overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-black mb-2 text-gray-900">{pkg.name}</h3>
                <p className="text-sm mb-4" style={{ color: '#666666' }}>{pkg.description}</p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black" style={{ color: '#B8860B' }}>
                      ${pkg.price}
                    </span>
                    <span className="text-base font-medium" style={{ color: '#888888' }}>
                      /{pkg.duration}
                    </span>
                  </div>
                </div>

                {/* Best For */}
                <div
                  className="rounded-lg px-3 py-2 mb-6 text-center"
                  style={{ background: '#B8860B18', border: '1px solid #B8860B33' }}
                >
                  <p className="text-xs font-bold" style={{ color: '#B8860B' }}>
                    BEST FOR: {pkg.bestFor}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm" style={{ color: '#555555' }}>
                      <i className="ri-checkbox-circle-fill text-base flex-shrink-0 mt-0.5" style={{ color: '#B8860B' }}></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href="#booking"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap hover:opacity-90"
                  style={{
                    background: pkg.popular
                      ? 'linear-gradient(90deg, #B8860B, #F5D76E)'
                      : '#f5f5f5',
                    color: pkg.popular ? '#000000' : '#333333',
                    border: pkg.popular ? 'none' : '1px solid #e0e0e0',
                  }}
                >
                  {pkg.cta}
                  <i className="ri-arrow-right-line text-base"></i>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <p className="text-center text-sm mt-12" style={{ color: '#888888' }}>
          <i className="ri-shield-check-line mr-1" style={{ color: '#B8860B' }}></i>
          All programs include a 90-day results guarantee. No long-term contracts.
        </p>

      </div>
    </section>
  );
}
